import { usePhotosStore } from "@/features/camera/hooks/usePhotoStore";
import { services as s } from "@/services";
import { Camera, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Webcam from "react-webcam";
import { toast } from "@patient-watch/ui/sonner";
import { getToastCopy } from "@/features/i18n/toastCopy";

import type { InputT } from "../../types/database/diaries";
import { ui } from "@patient-watch/ui";

interface Props {
    input: InputT;
}

function PhotoCamera({ onCapture, onClose }: { onCapture: (dataUrl: string) => void; onClose: () => void }) {
    const webcamRef = useRef<Webcam | null>(null);

    const handleCapture = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) return;
        onCapture(imageSrc);
        onClose();
    }, [onCapture, onClose]);

    return (
        <section className="top-0 left-0 z-50 fixed flex flex-col justify-between items-center bg-black w-[100vw] h-[100vh]">
            <div className="flex justify-between items-center px-4 py-3 w-full text-white">
                <p className="font-medium text-sm">Take a photo</p>
                <ui.Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-white hover:text-white"
                    onClick={onClose}
                >
                    Cancel
                </ui.Button>
            </div>
            <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "environment" }}
                className="w-full max-h-[70vh] object-cover"
            />
            <div className="px-4 py-6 w-full">
                <ui.Button type="button" className="w-full h-12" onClick={handleCapture}>
                    Capture photo
                </ui.Button>
            </div>
        </section>
    );
}

const Photo = (props: Props) => {
    const photos = usePhotosStore();
    const storedPhotos = usePhotosStore((state) => state.photos);
    const { setValue } = useFormContext();
    const inputId = props.input.input_id;
    const [cameraOpen, setCameraOpen] = useState(false);

    useEffect(() => {
        if (!inputId) return;
        setValue(
            inputId,
            storedPhotos.map((photo) => photo.img).filter((img) => img.length > 0),
            { shouldDirty: true }
        );
    }, [inputId, setValue, storedPhotos]);

    const addImage = (img: string) => {
        photos.addPhotos([
            {
                label: props.input.input_label ?? "",
                checked: false,
                delay: 0,
                img,
            },
        ]);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);

        const validFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
        const invalidFiles = selectedFiles.filter((file) => !file.type.startsWith("image/"));

        if (invalidFiles.length > 0) {
            toast.error(getToastCopy().pleaseSelectFile);
        }

        const validImgs = await Promise.all(
            validFiles.map(async (file) => {
                return s.convertFileToBase64(file);
            })
        );

        photos.addPhotos(
            validImgs.map((img) => ({
                label: props.input.input_label ?? "",
                checked: false,
                delay: 0,
                img,
            }))
        );
    };

    return (
        <div className="flex flex-col justify-center items-center gap-2">
            {cameraOpen ? (
                <PhotoCamera
                    onClose={() => setCameraOpen(false)}
                    onCapture={(dataUrl) => {
                        addImage(dataUrl);
                    }}
                />
            ) : null}
            <p className="px-4 text-md text-slate-700 text-center">
                Add photos with the camera, or choose a file from your device.
            </p>
            <ui.Button type="button" onClick={() => setCameraOpen(true)} className="w-full h-12" variant="secondary">
                <Camera />
                Camera
            </ui.Button>
            <b>OR</b>
            <ul className="flex flex-row flex-wrap gap-2 w-full">
                {storedPhotos.map((photo) => (
                    <li key={photo.id} className="relative">
                        <img className="rounded-lg w-full max-w-sm max-h-sm" src={photo.img} alt={photo.label} />
                        <ui.Button
                            type="button"
                            onClick={() => photos.removePhoto(photo.id)}
                            variant="outline"
                            size="icon"
                            className="top-0 right-0 absolute bg-opacity-90 m-2"
                            aria-label="Remove photo"
                        >
                            <Trash2 size={24} />
                        </ui.Button>
                    </li>
                ))}
            </ul>
            <ui.Input
                className="cursor-pointer"
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                multiple={props.input.allow_multiple}
                aria-label="Choose a photo"
                onChange={handleFileChange}
            />
        </div>
    );
};

export default Photo;
